import { TexturedMaterial } from './TexturedMaterial';
import { SimpleSettingsProperty } from '../properties/SimpleSettingsProperty';
import * as THREE from 'three';
import { Color } from '../types';
import { Logger } from '../core/Logger';
import { MaterialLoadingDebugger } from './MaterialLoadingDebugger';
import { MaterialLoadingManager } from './MaterialLoadingManager';
import { POIData } from '../poi/POIData';
import { TextureCache } from './TextureCache';


export class ImageMaterial extends TexturedMaterial {
    public readonly colorSpace: SimpleSettingsProperty<string>;
    public readonly generateMipmaps: SimpleSettingsProperty<boolean>;
    public readonly useBackgroundColor: SimpleSettingsProperty<boolean>;
    public naturalWidth: number = 0;
    public naturalHeight: number = 0;
    private _currentTextureUrl: string | null = null; // Track current URL for cache cleanup

    constructor(
        name: string = 'ImageMaterial',
        poiData: POIData
    ) {
        super('image', name, poiData);
        
        const group = "Image";
        this.colorSpace = new SimpleSettingsProperty('Color Space', null, 'srgb', group);
        this.generateMipmaps = new SimpleSettingsProperty('Generate Mipmaps', null, true, group);
        this.useBackgroundColor = new SimpleSettingsProperty('Use POI Color as Background', null, false, group);
        
        // Link to POI data
        this.useBackgroundColor.addParent(poiData.useBackgroundColor);
        
        this.setupPropertyListeners();

        if(this.uri.value) {
            //console.log(`🎯 [DEBUG] ${this.name}: Constructor - initial URI '${this.uri.value}' found, loading texture`);
            this.loadTexture(this.uri.value);
        } else {
            //console.log(`🔍 [DEBUG] ${this.name}: Constructor - no initial URI found`);
        }
    }

    protected setupPropertyListeners(): void {
        super.setupPropertyListeners(); // Sets up PBR listeners AND uri.on() in TexturedMaterial
        
        if(this._poiData) {
            this.useBackgroundColor.addParent(this._poiData.useBackgroundColor);
        }
        // Listeners for image-specific properties
        this.colorSpace.on(() => this.updateTextureProperties());
        this.generateMipmaps.on(() => this.updateTextureProperties());
        this.useBackgroundColor.on(() => this.updateMaterialBlending());
        
        // 🎯 Initial texture load if URL is already set - will be triggered by requestTextureLoad()
    }

    getThreeMaterial(): THREE.Material {
        const materialStart = performance.now();
        ////Logger.Instance.Debug(`🧱 [TIMING] getThreeMaterial() called at ${materialStart.toFixed(2)}ms`, 'MaterialLoading');
        
        // Check if we need background color support
        if (this.useBackgroundColor.value && this.hasTransparency()) {
            return this.createLayeredMaterial();
        }
        
        // Ensure the base material is created first by calling super.
        const materialCreateStart = performance.now();
        const material = super.getThreeMaterial() as THREE.MeshStandardMaterial;
        const materialCreateEnd = performance.now();
        
        ////Logger.Instance.Debug(`🧱 [TIMING] Base material created in ${(materialCreateEnd - materialCreateStart).toFixed(2)}ms`, 'MaterialLoading');

        // 🎯 FIX: Do NOT auto-load texture here - this prevents race conditions
        // Texture loading will be triggered explicitly after MaterialLoadingManager registration
        ////Logger.Instance.Debug(`🎯 [TIMING] Material created without auto-loading texture`, 'MaterialLoading');

        const materialEnd = performance.now();
        ////Logger.Instance.Debug(`🧱 [TIMING] getThreeMaterial() completed in ${(materialEnd - materialStart).toFixed(2)}ms`, 'MaterialLoading');
        
        return material;
    }

    /**
     * 🎯 Explicitly request texture loading - called after MaterialLoadingManager registration
     */
    public requestTextureLoad(): void {
        if (!this._texture && !this.isLoading && this.uri.value) {
            //console.log(`🚀 [DEBUG] ${this.name}: Loading texture '${this.uri.value}'`);
            this.loadTexture(this.uri.value);
        } else {
            //console.log(`🔍 [DEBUG] ${this.name}: Skipping texture load - texture=${!!this._texture}, isLoading=${this.isLoading}, uri='${this.uri.value}'`);
        }
    }

    private createLayeredMaterial(): THREE.Material {
        // For layered rendering, we'll use a MultiMaterial approach
        // This would require mesh-level support, so for now we'll modify the base material
        const material = super.getThreeMaterial() as THREE.MeshStandardMaterial;
        
        // Set POI color as background when using background mode
        // The POI color comes from the base PBR material's color property
        const poiColor = this.color.value;
        if (poiColor) {
            material.color.setRGB(poiColor.r, poiColor.g, poiColor.b);
        }
        
        // Load texture if needed
        if (!this._texture && !this.isLoading && this.uri.value) {
            this.loadTexture(this.uri.value);
        }
        
        return material;
    }

    private updateMaterialBlending(): void {
        if (this._threeMaterial) {
            if (this.useBackgroundColor.value && this.hasTransparency()) {
                // Only modify material when we have transparency AND useBackgroundColor is true
                const poiColor = this.color.value;
                if (poiColor) {
                    this._threeMaterial.color.setRGB(poiColor.r, poiColor.g, poiColor.b);
                }
                
                // Ensure proper blending for layered rendering
                this._threeMaterial.transparent = true;
                this._threeMaterial.alphaTest = 0;
            } else {
                // Reset to white when not using background color or no transparency
                this._threeMaterial.color.setRGB(1, 1, 1);
            }
            this._threeMaterial.needsUpdate = true;
        }
    }

    protected loadTexture(url: string): void {
        //console.log(`🎯 [DEBUG] ${this.name}: loadTexture() called with url='${url}'`);
        
        if (!url) {
            //console.log(`❌ [DEBUG] ${this.name}: Empty URL - disposing texture`);
            this.disposeTexture(); // Dispose of old texture if URL is cleared
            return;
        }

        //console.log(`🚀 [DEBUG] ${this.name}: Starting texture loading for '${url}'`);
        this.isLoading = true;
        this.error = null;
        
        const startTime = performance.now();
        //Logger.Instance.Debug(`🖼️ [TIMING] Starting to load texture: ${url} at ${startTime.toFixed(2)}ms`, 'MaterialLoading');
        
        // 🔍 DEBUG: Mark load start
        MaterialLoadingDebugger.Instance.markLoadStart(url);
        
        // 🎯 NEW: Use TextureCache for efficient texture sharing
        const loader = MaterialLoadingManager.isInitialized() 
            ? MaterialLoadingManager.Instance.getTextureLoader()
            : new THREE.TextureLoader();
        
        // Use TextureCache to get shared texture instance
        TextureCache.Instance.getTexture(url, loader)
            .then( 
            (texture: THREE.Texture) => {
                const loadCompleteTime = performance.now();
                const totalLoadTime = loadCompleteTime - startTime;
                
                //console.log(`✅ [DEBUG] ${this.name}: Texture obtained from cache! '${url}' (${totalLoadTime.toFixed(2)}ms)`);
                //console.log(`  📊 Texture info: ${texture.image.width}x${texture.image.height}, uuid=${texture.uuid}`);
                
                if (!this._threeMaterial) {
                    // Release our reference since we're not using it
                    TextureCache.Instance.releaseTexture(url);
                    ////Logger.Instance.Debug(`🗑️ [TIMING] Texture reference released (no material) at ${performance.now().toFixed(2)}ms`, 'MaterialLoading');
                    return;
                }
                
                const processingStart = performance.now();
                
                // Release old texture reference if we had one
                if (this._texture && this._currentTextureUrl) {
                    TextureCache.Instance.releaseTexture(this._currentTextureUrl);
                }
                
                this._texture = texture;
                this._currentTextureUrl = url; // Track current URL for cleanup
                this._threeMaterial.map = texture;
                
                //console.log(`🔗 [DEBUG] ${this.name}: Assigned shared texture to THREE.js material`);
                //console.log(`  📦 Material: ${this._threeMaterial.constructor.name}, uuid=${this._threeMaterial.uuid}`);
                //console.log(`  🖼️ Texture UUID: ${texture.uuid}, URL: ${url}`);
                //console.log(`  🔍 Material.map === texture: ${this._threeMaterial.map === texture}`);

                const hasAlpha = texture.format === THREE.RGBAFormat || texture.format === THREE.RGBA_S3TC_DXT5_Format;
                //console.log(`🔍 [DEBUG] ${this.name}: Transparency check - texture.format=${texture.format}, hasAlpha=${hasAlpha}`);
                //console.log(`🔍 [DEBUG] ${this.name}: Current transparent.value=${this.transparent.value}`);
                if (hasAlpha) {
                    //console.log(`✅ [DEBUG] ${this.name}: Alpha channel detected - setting transparent=true`);
                    this.transparent.value = true;
                } else {
                    //console.log(`❌ [DEBUG] ${this.name}: No alpha channel detected`);
                }
                //console.log(`🔍 [DEBUG] ${this.name}: Final transparent.value=${this.transparent.value}`);
                
                this.naturalWidth = texture.image.naturalWidth;
                this.naturalHeight = texture.image.naturalHeight;
                
                const calculatedAspectRatio = (this.naturalWidth > 0 && this.naturalHeight > 0)
                    ? this.naturalWidth / this.naturalHeight
                    : 1.0;
                    
                this.originalTextureAspectRatio = calculatedAspectRatio;
                this.aspectRatio.value = calculatedAspectRatio;

                const propertiesStart = performance.now();
                this.updateTextureProperties();
                const propertiesEnd = performance.now();
                
                this.isLoading = false;
                
                // Apply background color blending if enabled
                this.updateMaterialBlending();
                
                this._threeMaterial.needsUpdate = true;
                
                const processingEnd = performance.now();
                const processingTime = processingEnd - processingStart;
                const propertiesTime = propertiesEnd - propertiesStart;
                
                // 🔍 DEBUG: Mark completion with dimensions
                const dimensions = { width: this.naturalWidth, height: this.naturalHeight };
                MaterialLoadingDebugger.Instance.markCompleted(url, dimensions);
                
                // 🎯 FIX: Explicitly notify MaterialLoadingManager that texture is loaded
                // This ensures the loading manager knows about successful loads even if 
                // THREE.js LoadingManager events don't fire properly
                MaterialLoadingManager.Instance.markTextureLoaded(url);
                
                //Logger.Instance.Debug(`⚙️ [TIMING] Texture processing: ${processingTime.toFixed(2)}ms (properties: ${propertiesTime.toFixed(2)}ms)`, 'MaterialLoading');
                //Logger.Instance.Debug(`✅ [TIMING] TOTAL COMPLETE: ${url} loaded in ${(performance.now() - startTime).toFixed(2)}ms (${this.naturalWidth}x${this.naturalHeight})`, 'MaterialLoading');
            })
            .catch((err: unknown) => {
                const errorTime = performance.now() - startTime;
                console.error(`❌ [DEBUG] ${this.name}: Texture loading FAILED for '${url}' after ${errorTime.toFixed(2)}ms`);
                console.error(`❌ [DEBUG] Error details:`, err);
                
                this.error = typeof err === 'string' ? err : `Failed to load image: ${url}`;
                this.isLoading = false;
                
                // 🔍 DEBUG: Mark failed
                MaterialLoadingDebugger.Instance.markError(url, this.error);
                
                //Logger.Instance.Error(`❌ [TIMING] TOTAL ERROR: ${url} failed after ${errorTime.toFixed(2)}ms: ${this.error}`, 'MaterialLoading');
            });
    }
    
    /**
     * Enhanced transparency detection for image materials
     * Overrides the base implementation with more sophisticated detection
     */
    protected override hasTransparency(): boolean {
        if (this._texture){
        
        // Check texture format for alpha channel
        const hasAlphaFormat = this._texture.format === THREE.RGBAFormat || 
                              this._texture.format === THREE.RGBA_S3TC_DXT5_Format ||
                              this._texture.format === THREE.RGBA_ASTC_4x4_Format ||
                              this._texture.format === THREE.RGBA_BPTC_Format ||
                              this._texture.format === THREE.RGBA_ETC2_EAC_Format;
        
        // Also check if transparent was explicitly set (indicates alpha detected during load)
        const hasTransparentFlag = this.transparent.value === true;
        
        // Check if URL is PNG (likely to have transparency)
        const uri = this.uri.value?.toLowerCase() || '';
        const isPng = uri.endsWith('.png');
        
        return hasAlphaFormat || hasTransparentFlag || isPng;
    }

        // 2. Wenn Texture noch nicht geladen, verwende URL-Heuristik
        const uri = this.uri.value?.toLowerCase() || '';
        const isPng = uri.endsWith('.png');
        const isWebp = uri.endsWith('.webp');
        const isGif = uri.endsWith('.gif');
        const isSvg = uri.endsWith('.svg');
        
        return isPng || isWebp || isGif || isSvg;


    }

    protected disposeTexture(): void {
        // 🎯 Release texture cache reference if we have one
        if (this._currentTextureUrl) {
            TextureCache.Instance.releaseTexture(this._currentTextureUrl);
            this._currentTextureUrl = null;
        }
        
        // Clear our texture reference (don't dispose - cache manages that)
        if (this._texture) {
            this._texture = null;
        }
        if (this._threeMaterial) {
            this._threeMaterial.map = null;
            this._threeMaterial.needsUpdate = true;
        }
        this.aspectRatio.value = 1.0;
    }

    protected updateTextureProperties(): void {
        if (this._texture && this._threeMaterial) {
            // textureFit=contain legt das Bild kleiner als die Flaeche an — UVs laufen
            // dann ueber [0,1] hinaus. Mit dem Default 'repeat' wuerde das Bild dort
            // KACHELN; geklemmt liefert der (transparente) Bildrand die Leerflaeche.
            const containFit = this._poiData?.textureFit?.value === 'contain';
            this._texture.wrapS = containFit
                ? THREE.ClampToEdgeWrapping
                : this.getWrapConstant(this.wrapS.value ?? 'repeat');
            this._texture.wrapT = containFit ? THREE.ClampToEdgeWrapping : this.getWrapConstant(this.wrapT.value ?? 'repeat');
            this._texture.magFilter = this.getFilterConstant(this.magFilter.value ?? 'linear');
            this._texture.minFilter = this.getFilterConstant(this.minFilter.value ?? 'linearMipmapLinear');
            this._texture.flipY = this.flipY.value ?? true;
            this._texture.repeat.set(this.repeatX.value ?? 1, this.repeatY.value ?? 1);
            this._texture.offset.set(this.offsetX.value ?? 0, this.offsetY.value ?? 0);
            this._texture.rotation = (this.rotation.value ?? 0) * (Math.PI / 180);
            this._texture.center.set(0.5, 0.5);
            this._texture.colorSpace = this.colorSpace.value ?? 'srgb';
            this._texture.generateMipmaps = this.generateMipmaps.value ?? true;
            this._texture.lodbias = this.lodBias.value ?? 0;
            this._texture.needsUpdate = true;
            this._threeMaterial.needsUpdate = true;
        }
    }

    private getWrapConstant(wrap: string): number {
        switch (wrap) {
            case 'repeat': return THREE.RepeatWrapping;
            case 'clampToEdge': return THREE.ClampToEdgeWrapping;
            case 'mirroredRepeat': return THREE.MirroredRepeatWrapping;
            default: return THREE.RepeatWrapping;
        }
    }
    
    private getFilterConstant(filter: string): number {
        switch (filter) {
            case 'nearest': return THREE.NearestFilter;
            case 'linear': return THREE.LinearFilter;
            case 'nearestMipmapNearest': return THREE.NearestMipmapNearestFilter;
            case 'linearMipmapNearest': return THREE.LinearMipmapNearestFilter;
            case 'nearestMipmapLinear': return THREE.NearestMipmapLinearFilter;
            case 'linearMipmapLinear': return THREE.LinearMipmapLinearFilter;
            default: return THREE.LinearFilter;
        }
    }
} 